"use client"

import React, { useState, useRef, useEffect } from "react"
import Image from "next/image"
import { cn } from "@/lib/utils"

export interface MediaContainerProps {
  src?: string
  dataSrc?: string // For lazy loading
  type: "video" | "image" | "gif"
  alt?: string
  className?: string
  autoPlay?: boolean
  loop?: boolean
  muted?: boolean
  playsInline?: boolean
  controls?: boolean
  width?: number
  height?: number
  priority?: boolean
  fallbackText?: string
  loading?: 'lazy' | 'eager'
}

// Optimized MediaContainer with intersection observer
const MediaContainer: React.FC<MediaContainerProps> = ({
  src,
  dataSrc,
  type,
  alt = "Media content",
  className,
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true,
  controls = false,
  width = 800,
  height = 600,
  priority = false,
  fallbackText = "Your browser does not support this media type.",
  loading = 'lazy'
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const defaultClasses =
    "w-full md:max-w-2xl h-auto rounded-t-[10px] shadow-[0_15px_40px_rgba(0,0,0,0.5)]"

  // // Intersection Observer for lazy loading videos
  // useEffect(() => {
  //   if (loading === 'eager' || type !== 'video') return

  //   const observer = new IntersectionObserver(
  //     (entries) => {
  //       const [entry] = entries
  //       if (entry.isIntersecting) {
  //         setIsInView(true)
  //         setVideoSrc(src)
  //         observer.disconnect()
  //       }
  //     },
  //     {
  //       rootMargin: '100px', // Start loading 100px before video comes into view
  //       threshold: 0.1
  //     }
  //   )

  //   if (containerRef.current) {
  //     observer.observe(containerRef.current)
  //   }

  //   return () => observer.disconnect()
  // }, [loading, src, type])

  // Video component
  if (type === "video") {
    return (
      <div ref={containerRef} className={cn(defaultClasses, className)}>
          <video
            className="w-full h-full rounded-t-[10px]"
            autoPlay={autoPlay}
            loop={loop}
            muted={muted}
            playsInline={playsInline}
            controls={controls}
            src={src}
            data-src={dataSrc}
            preload="metadata"
          >
            <source src={src} type="video/mp4" />
          </video>
       
      </div>
    )
  }

  // Image or GIF component (handled the same way by Next.js Image)
  if (type === "image" || type === "gif") {
    return (
      <Image
        src={src || "/placeholder.svg"}
        alt={alt}
        width={width}
        height={height}
        className={cn(defaultClasses, className)}
        loading={loading}
        priority={priority}
        unoptimized={type === "gif"} // Prevent optimization for GIFs to maintain animation
      />
    )
  }

  // Fallback
  return (
    <div
      className={cn(
        defaultClasses,
        "flex items-center justify-center bg-gray-800 text-white",
        className
      )}
    >
        <p>Unsupported media type: {type}</p>
    </div>
  )
}

export default MediaContainer
